import { Alert } from './Alert.js';
import { UpgradePrompt } from './UpgradePrompt.js';
import { ApiError } from '../api/client.js';
import type { SubscriptionTier } from '../api/subscriptions.js';

/**
 * The one place a caught request failure becomes UI. Pass the raw thrown value
 * — react-query's `mutation.error`, a caught `unknown`, or a message string —
 * and it renders the right surface:
 *
 *   - 402 PLAN_REQUIRED  → <UpgradePrompt> (an upsell, routed to /billing) —
 *                          NOT red, because a plan gate is not an error.
 *   - anything else      → <Alert variant="error"> with the server's message.
 *   - null / undefined   → nothing.
 *
 * Prefer passing the raw error (e.g. `mutation.error`) over a pre-stringified
 * message: the plan-gate detection needs the ApiError's status + body, which a
 * string throws away.
 */

interface PlanRequiredBody {
  error?: string;
  message?: string;
  missingFeature?: string;
  requiredTier?: SubscriptionTier | null;
}

function asPlanRequired(err: ApiError): PlanRequiredBody | null {
  if (err.status !== 402) return null;
  const body = err.body;
  if (body && typeof body === 'object' && (body as PlanRequiredBody).error === 'PLAN_REQUIRED') {
    return body as PlanRequiredBody;
  }
  return null;
}

/** Best-effort human message from any thrown value. */
function messageOf(err: unknown, fallback: string): string {
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

/**
 * The string form, for components that take an `errorMessage: string | null`
 * prop (modals like ReasonModal / MarkPaidModal) and can't host a component.
 * Returns null for no-error so the modal stays clean. `ApiError` extends Error,
 * so its server message flows through messageOf.
 */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string | null {
  if (error === null || error === undefined || error === '') return null;
  return messageOf(error, fallback);
}

export function ApiErrorNotice({
  error,
  fallback = 'Something went wrong. Please try again.',
  compact = false,
  className,
}: {
  error: unknown;
  fallback?: string;
  /** Passed to <Alert> for inline/in-row placement. The upgrade prompt (402)
   * is always the full card — a plan gate is worth the space. */
  compact?: boolean;
  className?: string;
}): JSX.Element | null {
  if (error === null || error === undefined || error === '') return null;

  if (error instanceof ApiError) {
    const gate = asPlanRequired(error);
    if (gate) {
      return <UpgradePrompt requiredTier={gate.requiredTier} missingFeature={gate.missingFeature} className={className} />;
    }
  }

  return (
    <Alert variant="error" compact={compact} className={className}>
      {messageOf(error, fallback)}
    </Alert>
  );
}
